import {useEffect, useRef} from 'react'
import toast from 'react-hot-toast'
import * as UC from '@uploadcare/file-uploader'
// Types
import {MAX_UPLOAD_FILE_SIZE_BYTES} from '../../consts/uploads'
import type {StripePayload, UploadcareFile} from '../../types/submission-wizard'
// Utils
import {logDataByLevel} from '../../utils/logging-helpers'
// Styles
import styles from '../submission-wizard/uploader-inline-step.module.scss'
import wizardStyles from '../submission-wizard/submission-wizard.module.scss'

interface UploadcareGroupResponse {
  id: string
  [key: string]: unknown
}

interface UploaderInlineStepProps {
  stripePayload: StripePayload
  sendStripePayload: (payload: StripePayload) => void
  uploadcareFiles: UploadcareFile[]
  sendUploadcareFiles: (files: UploadcareFile[]) => void
  usesMultipleUpload?: boolean
}

const UPLOAD_ERROR_MESSAGE = 'We couldn’t process your uploaded photos. Please try uploading again.'

const createGroupUrl = async (files: UploadcareFile[]): Promise<string | undefined> => {
  if (!files || files.length === 0) return undefined
  const formData = new FormData()
  formData.append('pub_key', process.env.NEXT_PUBLIC_UPLOADCARE_KEY || '')
  files.forEach(file => formData.append('files[]', file.uuid))

  try {
    const response = await fetch('https://upload.uploadcare.com/group/', {
      method: 'POST',
      body: formData,
    })
    // fetch() only rejects on network errors; a non-2xx (bad pub_key, invalid
    // file list) still resolves, so check explicitly — otherwise we'd build a
    // broken "ucarecdn.com/undefined/" URL and silently block submission.
    if (!response.ok) {
      logDataByLevel({level: 'error', message: 'Uploadcare group creation failed (non-OK response)', data: {status: response.status}})
      toast.error(UPLOAD_ERROR_MESSAGE)
      return undefined
    }
    const data: UploadcareGroupResponse = await response.json()
    if (!data?.id) {
      logDataByLevel({level: 'error', message: 'Uploadcare group response missing id', data})
      toast.error(UPLOAD_ERROR_MESSAGE)
      return undefined
    }
    return `https://ucarecdn.com/${data.id}/`
  } catch (error) {
    logDataByLevel({level: 'error', message: 'Error creating group:', data: error})
    // Surface to the user: a failed group means the submission URL stays empty
    // and they'd otherwise be silently blocked from completing the entry.
    toast.error(UPLOAD_ERROR_MESSAGE)
    return undefined
  }
}

const UploaderInlineStep = ({
  stripePayload,
  sendStripePayload,
  uploadcareFiles,
  sendUploadcareFiles,
  usesMultipleUpload = false,
}: UploaderInlineStepProps) => {
  UC.defineComponents(UC)

  const ctxProviderRef = useRef<(HTMLElement & { addEventListener: any; removeEventListener: any }) | null>(null)
  const uploaderRef = useRef<HTMLElement | null>(null)

  const stripePayloadRef = useRef(stripePayload)
  const uploadcareFilesRef = useRef(uploadcareFiles)
  stripePayloadRef.current = stripePayload
  uploadcareFilesRef.current = uploadcareFiles

  useEffect(() => {
    const ctxProvider = ctxProviderRef.current
    if (!ctxProvider) return

    const handleUploadSuccessEvent = async (uploadSuccessEvent: CustomEvent) => {
      const newFiles = [...uploadSuccessEvent.detail.allEntries.filter((file: UploadcareFile) => file.status === 'success')] as UploadcareFile[]
      sendUploadcareFiles(newFiles)

      let newSubmissionUrl = newFiles[0]?.cdnUrl || ''
      if (newFiles.length > 1) {
        newSubmissionUrl = await createGroupUrl(newFiles) || ''
      }
      sendStripePayload({...stripePayloadRef.current, submissionUrl: newSubmissionUrl})
    }
    const handleRemoveEvent = (removeEvent: CustomEvent) => {
      const newFiles = uploadcareFilesRef.current.filter((file: UploadcareFile) => file.cdnUrl !== removeEvent.detail.cdnUrl)
      sendUploadcareFiles(newFiles)
    }

    ctxProvider.addEventListener('common-upload-success', handleUploadSuccessEvent)
    ctxProvider.addEventListener('file-removed', handleRemoveEvent)
    return () => {
      ctxProvider.removeEventListener('common-upload-success', handleUploadSuccessEvent)
      ctxProvider.removeEventListener('file-removed', handleRemoveEvent)
    }
  }, [sendUploadcareFiles, sendStripePayload])

  return <>
    <div className={wizardStyles['__step-container']}>
      <div
        className={`
          ${styles['uploader-inline-step']}
          ${wizardStyles['__submission-wizard-step']}
        `}
      >
        <uc-upload-ctx-provider
          ref={ctxProviderRef}
          ctx-name="submission-uploader"
        />
        <uc-config
          ctx-name="submission-uploader"
          pubkey={process.env.NEXT_PUBLIC_UPLOADCARE_KEY}
          img-only={true}
          multiple={usesMultipleUpload}
          multiple-max={`${usesMultipleUpload ? '0' : '1'}`}
          max-local-file-size-bytes={MAX_UPLOAD_FILE_SIZE_BYTES}
          remove-copyright={true}
        />
        <uc-file-uploader-inline
          ref={uploaderRef}
          ctx-name="submission-uploader"
          class="uc-light submission-uploader-inline"
        />
      </div>
    </div>
  </>
}

export default UploaderInlineStep
